import { getTokens, clearTokens, LOGOUT_ERROR_KEY, USERNAME_HEADER, REFRESH_TOKEN_HEADER, NetworkClientBuilder } from 'auth';
import jwtDecode from 'jwt-decode';
import { RCOrganization } from '@rcenter/core';
import { BACKOFFICE_DATA_ADDED, VerificationResult } from './types';
import { environment } from '../../../../environments/environment';
import { EStorageKeys } from '@app/react/types/enums';
import { ORGANIZATION_ID } from '@app/react/lib/localStorageKeys';
import { buildQueryString } from '@app/react/lib/urlUtils';
import { shouldCommitOrganizationSwitch } from '@app/react/components/sideMenu/organizationSwitchCommit';

export interface IAuthTokenData {
	id?: string;
	email?: string;
	firstName?: string;
	lastName?: string;
}

export enum PermissionsLoadStatusEnum {
	IDLE = 'idle',
	LOADING = 'loading',
	READY = 'ready',
	ERROR = 'error',
}

type OrganizationListener = (organization: RCOrganization | null) => void;
type PermissionsListener = (permissions: string[]) => void;
type PermissionsStatusListener = (status: PermissionsLoadStatusEnum) => void;
type OrganizationsCountListener = (count: number) => void;

interface ILogoutResponse {
	redirectUri?: string;
}

/**
 * Accepts same-origin URLs and relative paths. Rejects `javascript:`, protocol-relative, and off-origin values.
 */
function isSafeLogoutRedirectUri(redirectUri: string): boolean {
	if (redirectUri.startsWith('/') && !redirectUri.startsWith('//')) {
		return true;
	}

	try {
		const parsed = new URL(redirectUri);
		return parsed.origin === window.location.origin;
	} catch {
		return false;
	}
}

/**
 * Reads a logout redirect URI from an untrusted JSON body.
 */
function readLogoutRedirectUri(body: unknown): string | undefined {
	if (typeof body !== 'object' || body === null || !('redirectUri' in body)) {
		return undefined;
	}
	const redirectUri = (body as ILogoutResponse).redirectUri;
	if (typeof redirectUri !== 'string' || !isSafeLogoutRedirectUri(redirectUri)) {
		return undefined;
	}
	return redirectUri;
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class AuthWorker {
	private static currentOrganization: RCOrganization | null = null;
	private static permissions: string[] = [];
	private static permissionsStatus: PermissionsLoadStatusEnum = PermissionsLoadStatusEnum.IDLE;
	private static userOrganizationsCount = 0;
	private static organizationSwitchTargetId: number | undefined;
	private static organizationListeners = new Set<OrganizationListener>();
	private static permissionsListeners = new Set<PermissionsListener>();
	private static permissionsStatusListeners = new Set<PermissionsStatusListener>();
	private static organizationsCountListeners = new Set<OrganizationsCountListener>();

	public static hasBondUserDetails(): boolean {
		return !!AuthWorker.getBondUserDetails();
	}

	public static getBondUserEmail(): string | undefined {
		const userDetails = AuthWorker.getBondUserDetails();
		return userDetails?.email;
	}

	public static getBondUserDetails(): VerificationResult | undefined {
		const { userIdToken } = AuthWorker.getTokens();

		if (!userIdToken) {
			return;
		}

		try {
			return jwtDecode(userIdToken);
		} catch (e) {
			console.info('Error while decoding token', e);
			return;
		}
	}

	public static hasBondUserDetailsSubmitted(): boolean {
		const userDetails: VerificationResult = AuthWorker.getBondUserDetails();
		return !!userDetails?.[BACKOFFICE_DATA_ADDED];
	}

	static getTokens() {
		return getTokens();
	}

	/**
	 * Returns the decoded token fields used by layout chrome (name, email, id).
	 */
	static getTokenData(): IAuthTokenData {
		const tokenData: VerificationResult | undefined = AuthWorker.getBondUserDetails();

		if (!tokenData) {
			return {};
		}

		return {
			id: tokenData.id,
			email: tokenData.email,
			firstName: tokenData.given_name,
			lastName: tokenData.family_name,
		};
	}

	static getCurrentOrganization(): RCOrganization | null {
		return AuthWorker.currentOrganization;
	}

	/**
	 * Pins chrome writes to this organization until a newer switch starts or this one aborts.
	 * Successful switches keep the pin: releasing it would let a stale pre-switch bootstrap
	 * overwrite currentOrganization. The pin is tab-local in-memory state.
	 */
	static beginOrganizationSwitch(organizationId: number): void {
		AuthWorker.organizationSwitchTargetId = organizationId;
	}

	/**
	 * Clears the switch pin only when this organization is still the active target.
	 * If grants are still LOADING, mark ERROR so SideNav can offer Retry instead of
	 * staying on an empty rail after a discarded in-flight write.
	 */
	static releaseOrganizationSwitch(organizationId: number): void {
		if (AuthWorker.organizationSwitchTargetId !== organizationId) {
			return;
		}
		AuthWorker.organizationSwitchTargetId = undefined;
		AuthWorker.failPendingPermissionsLoad();
	}

	static getOrganizationSwitchTargetId(): number | undefined {
		return AuthWorker.organizationSwitchTargetId;
	}

	/**
	 * Stale overlapping bootstraps must not write AuthWorker or `/client` would load the wrong grants.
	 */
	static canCommitOrganizationSwitch(organizationId: number): boolean {
		return shouldCommitOrganizationSwitch(organizationId, AuthWorker.organizationSwitchTargetId);
	}

	/**
	 * Stores the active organization for Angular layout and React islands, and persists the org id.
	 * Changing org id clears permissions so SideNav does not keep the previous org's grants.
	 */
	static setCurrentOrganization(organization: RCOrganization | null): void {
		const previousOrganizationId = AuthWorker.currentOrganization?.id;
		const organizationChanged = previousOrganizationId !== organization?.id;
		AuthWorker.currentOrganization = organization;
		localStorage.removeItem(EStorageKeys.BN_CART);

		if (organization) {
			localStorage.setItem(ORGANIZATION_ID, String(organization.id));
		}

		if (organizationChanged) {
			AuthWorker.permissions = [];
			AuthWorker.permissionsStatus = PermissionsLoadStatusEnum.LOADING;
		}

		AuthWorker.organizationListeners.forEach(listener => listener(organization));

		if (organizationChanged) {
			AuthWorker.permissionsListeners.forEach(listener => listener([]));
			AuthWorker.notifyPermissionsStatus();
		}
	}

	static subscribeToOrganization(listener: OrganizationListener): () => void {
		AuthWorker.organizationListeners.add(listener);
		return () => {
			AuthWorker.organizationListeners.delete(listener);
		};
	}

	static getPermissions(): string[] {
		return AuthWorker.permissions;
	}

	/**
	 * Stores permissions for the active organization. Writes for a different org are ignored
	 * so an in-flight fetch cannot overwrite the current org after a switch.
	 */
	static setPermissions(permissions: string[], organizationId: number): void {
		if (AuthWorker.currentOrganization?.id !== organizationId) {
			console.warn(
				`AuthWorker.setPermissions ignored a write for organization ${organizationId}; current organization is ${
					AuthWorker.currentOrganization?.id ?? 'unset'
				}.`
			);
			return;
		}
		AuthWorker.permissions = permissions;
		AuthWorker.permissionsStatus = PermissionsLoadStatusEnum.READY;
		AuthWorker.permissionsListeners.forEach(listener => listener(permissions));
		AuthWorker.notifyPermissionsStatus();
	}

	/**
	 * Marks the current organization's grants as failed so SideNav can retry.
	 * Writes for a different org are ignored. The switch pin is not checked: a
	 * newer in-flight switch that later commits will reset status to LOADING.
	 */
	static setPermissionsError(organizationId: number): void {
		if (AuthWorker.currentOrganization?.id !== organizationId) {
			return;
		}
		AuthWorker.permissionsStatus = PermissionsLoadStatusEnum.ERROR;
		AuthWorker.notifyPermissionsStatus();
	}

	static getPermissionsStatus(): PermissionsLoadStatusEnum {
		return AuthWorker.permissionsStatus;
	}

	/**
	 * An aborted switch can leave the committed org in LOADING after `/client`
	 * discarded a stale grants write via `canCommit`. Surface ERROR so Retry appears.
	 */
	private static failPendingPermissionsLoad(): void {
		if (AuthWorker.permissionsStatus !== PermissionsLoadStatusEnum.LOADING) {
			return;
		}
		AuthWorker.permissionsStatus = PermissionsLoadStatusEnum.ERROR;
		AuthWorker.notifyPermissionsStatus();
	}

	static subscribeToPermissions(listener: PermissionsListener): () => void {
		AuthWorker.permissionsListeners.add(listener);
		return () => {
			AuthWorker.permissionsListeners.delete(listener);
		};
	}

	static subscribeToPermissionsStatus(listener: PermissionsStatusListener): () => void {
		AuthWorker.permissionsStatusListeners.add(listener);
		return () => {
			AuthWorker.permissionsStatusListeners.delete(listener);
		};
	}

	private static notifyPermissionsStatus(): void {
		AuthWorker.permissionsStatusListeners.forEach(listener => listener(AuthWorker.permissionsStatus));
	}

	static getUserOrganizationsCount(): number {
		return AuthWorker.userOrganizationsCount;
	}

	static setUserOrganizationsCount(count: number): void {
		AuthWorker.userOrganizationsCount = count;
		AuthWorker.organizationsCountListeners.forEach(listener => listener(count));
	}

	static subscribeToUserOrganizationsCount(listener: OrganizationsCountListener): () => void {
		AuthWorker.organizationsCountListeners.add(listener);
		return () => {
			AuthWorker.organizationsCountListeners.delete(listener);
		};
	}

	/**
	 * Revokes the refresh token, clears local session state, and redirects to login.
	 */
	static logout(error?: string): void {
		const tokens = AuthWorker.getTokens();

		localStorage.removeItem(ORGANIZATION_ID);
		localStorage.removeItem('lastRefreshedTimestamp');
		localStorage.removeItem('id_token');
		localStorage.removeItem(EStorageKeys.BN_CART);
		AuthWorker.currentOrganization = null;
		AuthWorker.permissions = [];
		AuthWorker.permissionsStatus = PermissionsLoadStatusEnum.IDLE;
		AuthWorker.userOrganizationsCount = 0;
		AuthWorker.organizationSwitchTargetId = undefined;
		AuthWorker.organizationListeners.forEach(listener => listener(null));
		AuthWorker.permissionsListeners.forEach(listener => listener([]));
		AuthWorker.notifyPermissionsStatus();
		AuthWorker.organizationsCountListeners.forEach(listener => listener(0));

		clearTokens();

		if (window['Intercom']) {
			window['Intercom']('shutdown');
		}

		const logoutRedirectUri = `${window.location.origin}/login`;
		const query = buildQueryString({ redirectUri: logoutRedirectUri, reason: error });

		void (async () => {
			let redirectUri = logoutRedirectUri;
			try {
				const response = await fetch(`${environment.CS_URLS.AUTH_ROOT}/logout${query}`, {
					method: 'DELETE',
					credentials: NetworkClientBuilder.includeCredentials ? 'include' : 'same-origin',
					headers: {
						...(tokens.refreshToken && { [REFRESH_TOKEN_HEADER]: tokens.refreshToken }),
						...(tokens.username && { [USERNAME_HEADER]: tokens.username }),
					},
				});
				const parsedRedirectUri = readLogoutRedirectUri(await response.json());
				if (parsedRedirectUri) {
					redirectUri = parsedRedirectUri;
				}
			} catch (error) {
				console.error('Logout request failed', error);
				redirectUri = logoutRedirectUri;
			}

			if (error) {
				sessionStorage.setItem(LOGOUT_ERROR_KEY, error);
			}

			window.location.href = redirectUri;
		})();
	}
}
